import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Palette, MapPin, Phone, Mail, Globe, Loader2, Plus, Edit, Trash2, Eye, EyeOff,
  Save, Crown, BarChart3, Star, MessageCircle, Calendar, Clock, DollarSign,
  CheckCircle, Image, Video, ExternalLink, Sparkles, Settings, TrendingUp, Upload, X, Target, CreditCard, Users, BadgeCheck
} from 'lucide-react';
import { AIDescriptionButton } from '../components/AIDescriptionButton';
import authAxios, { API } from '../utils/authAxios';
import { CHILEAN_REGIONS, getCitiesByRegion } from '../constants/chileanLocations';

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt', 'Viña del Mar', 'Iquique', 'Calama', 'Copiapó', 'Coquimbo', 'Chillán', 'Osorno', 'Valdivia', 'Punta Arenas'];

const workshopTypeOptions = [
  { value: 'prevencion', label: 'Prevención' },
  { value: 'tratamiento', label: 'Tratamiento' },
  { value: 'rehabilitacion', label: 'Rehabilitación' },
  { value: 'reinsercion', label: 'Reinserción' },
  { value: 'apoyo_familiar', label: 'Apoyo Familiar' },
  { value: 'grupos_ayuda', label: 'Grupos de Ayuda' },
  { value: 'capacitacion', label: 'Capacitación' },
  { value: 'charla', label: 'Charla' },
  { value: 'seminario', label: 'Seminario' },
  { value: 'retiro', label: 'Retiro' },
  { value: 'terapia_grupal', label: 'Terapia Grupal' },
  { value: 'arte_terapia', label: 'Arte-Terapia' },
  { value: 'teatro', label: 'Teatro' },
  { value: 'meditacion', label: 'Meditación' },
  { value: 'yoga', label: 'Yoga' },
  { value: 'deportes', label: 'Deportes' },
  { value: 'otro', label: 'Otro' }
];

const modalityOptions = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'online', label: 'Online' },
  { value: 'hibrido', label: 'Híbrido' }
];

const additionalServicesOptions = [
  'Materiales incluidos',
  'Certificado de participación',
  'Seguimiento personalizado',
  'Acceso a comunidad',
  'Recursos digitales',
  'Sesiones de práctica',
  'Grabaciones disponibles',
  'Refrigerio incluido',
  'Transporte'
];

// Initial form state
const initialFormState = {
  name: '',
  description: '',
  workshop_type: 'otro',
  type: '',
  custom_type: '',
  target_audience: '',
  phone: '',
  whatsapp: '',
  email: '',
  website: '',
  address: '',
  city: '',
  region: '',
  facebook: '',
  instagram: '',
  modality: 'presencial',
  price: '',
  price_description: '',
  is_free: false,
  logo: '',
  gallery: [],
  youtube_video: '',
  schedule: '',
  duration: '',
  capacity: '',
  requirements: '',
  additional_services: [],
  is_public: false
};

// Preview Component - Shows how the workshop will look in the public directory
function WorkshopPreview({ data }) {
  if (!data || !data.name) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-12 text-center">
          <Eye className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">Completa los datos para ver la vista previa</p>
        </CardContent>
      </Card>
    );
  }

  var workshopImage = data.logo || (data.gallery && data.gallery[0]);
  var workshopType = workshopTypeOptions.find(function(t) { return t.value === data.workshop_type; });
  var workshopTypeName = workshopType ? workshopType.label : data.custom_type || 'Taller';
  var modalityLabel = data.modality === 'presencial' ? 'Presencial' : data.modality === 'online' ? 'Online' : 'Híbrido';

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-slate-50 flex items-center gap-2">
          <Eye className="w-5 h-5 text-purple-400" />
          Vista Previa del Taller
        </CardTitle>
        <p className="text-slate-400 text-sm">Así se verá tu taller en el directorio público</p>
      </CardHeader>
      <CardContent>
        {/* Preview Card */}
        <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-md mx-auto">
          {/* Image */}
          <div className="aspect-video bg-slate-100 relative overflow-hidden">
            {workshopImage ? (
              <img src={workshopImage} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Palette className="w-16 h-16 text-slate-300" />
              </div>
            )}
            {/* Badges */}
            <div className="absolute top-3 left-3 flex gap-2">
              {data.is_premium && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
              {data.is_free && (
                <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-md shadow-md">
                  Gratuito
                </span>
              )}
            </div>
            {/* Modality Badge */}
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-md">
                {modalityLabel}
              </span>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-5">
            {/* Type Badge */}
            <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full mb-2">
              {workshopTypeName}
            </span>
            
            {/* Location */}
            {data.city && (
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                <MapPin className="w-4 h-4 text-purple-500" />
                <span>{data.city}</span>
              </div>
            )}
            
            {/* Name */}
            <h3 className="font-semibold text-lg text-slate-800 mb-2 line-clamp-2">
              {data.name}
            </h3>
            
            {/* Description */}
            {data.description && (
              <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                {data.description}
              </p>
            )}
            
            {/* Additional Services */}
            {data.additional_services && data.additional_services.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {data.additional_services.slice(0, 3).map(function(service, idx) {
                  return (
                    <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {service}
                    </span>
                  );
                })}
                {data.additional_services.length > 3 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    +{data.additional_services.length - 3}
                  </span>
                )}
              </div>
            )}
            
            {/* Schedule & Duration */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
              {data.schedule && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {data.schedule}
                </span>
              )}
              {data.duration && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {data.duration}
                </span>
              )}
            </div>
            
            {/* Price & Capacity */}
            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              {data.is_free ? (
                <span className="text-emerald-600 font-semibold">Gratuito</span>
              ) : data.price ? (
                <span className="flex items-center gap-1 text-slate-700 font-medium">
                  <DollarSign className="w-4 h-4" />
                  ${Number(data.price).toLocaleString('es-CL')}
                  {data.price_description && <span className="text-slate-500 font-normal text-xs">/{data.price_description}</span>}
                </span>
              ) : (
                <span className="text-slate-500 text-sm">Consultar precio</span>
              )}
              
              {data.capacity && (
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Users className="w-4 h-4" />
                  Máx. {data.capacity}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function WorkshopDashboard() {
  var auth = useAuth();
  var [searchParams] = useSearchParams();
  var [activeTab, setActiveTab] = useState('workshops');
  var [workshops, setWorkshops] = useState([]);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [editingWorkshop, setEditingWorkshop] = useState(null);
  var [formData, setFormData] = useState(initialFormState);
  var [showForm, setShowForm] = useState(false);
  var [analytics, setAnalytics] = useState(null);
  var [premiumStatus, setPremiumStatus] = useState(null);
  var [uploading, setUploading] = useState(false);
  var galleryInputRef = useRef(null);

  // Check for premium success/cancel in URL
  useEffect(function() {
    if (searchParams.get('premium_success') === '1') {
      toast.success('¡Plan Premium activado exitosamente!');
      var workshopId = searchParams.get('workshop_id');
      if (workshopId) {
        loadPremiumStatus(workshopId);
      }
    }
    if (searchParams.get('premium_cancelled') === '1') {
      toast.info('Pago cancelado');
    }
  }, [searchParams]);

  useEffect(function() {
    loadWorkshops();
  }, []);

  async function loadWorkshops() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/workshop/my-workshops', {
        headers: { Authorization: 'Bearer ' + auth.token }
      });
      setWorkshops(res.data.workshops || []);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar talleres');
    } finally {
      setLoading(false);
    }
  }

  async function loadAnalytics(workshopId) {
    try {
      var res = await authAxios.get(API + '/workshop/' + workshopId + '/analytics', {
        headers: { Authorization: 'Bearer ' + auth.token }
      });
      setAnalytics(res.data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadPremiumStatus(workshopId) {
    try {
      var res = await authAxios.get(API + '/workshop/' + workshopId + '/premium-status', {
        headers: { Authorization: 'Bearer ' + auth.token }
      });
      setPremiumStatus(res.data);
    } catch (e) {
      console.error(e);
    }
  }

  function handleChange(e) {
    var name = e.target.name;
    var value = e.target.value;
    setFormData(function(prev) {
      return Object.assign({}, prev, { [name]: value });
    });
  }

  function handleSwitchChange(name, value) {
    setFormData(function(prev) {
      return Object.assign({}, prev, { [name]: value });
    });
  }

  function handleServiceToggle(service) {
    setFormData(function(prev) {
      var services = prev.additional_services || [];
      if (services.includes(service)) {
        return Object.assign({}, prev, { additional_services: services.filter(function(s) { return s !== service; }) });
      } else {
        return Object.assign({}, prev, { additional_services: services.concat([service]) });
      }
    });
  }

  function handleGalleryChange(e) {
    var value = e.target.value;
    var urls = value.split('\n').filter(function(u) { return u.trim(); });
    setFormData(function(prev) {
      return Object.assign({}, prev, { gallery: urls });
    });
  }

  async function handleImageUpload(e, type) {
    var file = e.target.files?.[0];
    if (!file) return;
    
    // Client-side validation
    var maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('La imagen es muy grande. Máximo 10MB');
      return;
    }
    
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    var allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    var ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }
    
    setUploading(true);
    try {
      var formDataUpload = new FormData();
      formDataUpload.append('file', file);
      var res = await authAxios.post(API + '/upload/image', formDataUpload, { withCredentials: true });
      var imageUrl = res.data.url;
      if (type === 'logo') {
        setFormData(function(prev) { return Object.assign({}, prev, { logo: imageUrl }); });
      } else {
        setFormData(function(prev) {
          var gallery = prev.gallery || [];
          return Object.assign({}, prev, { gallery: gallery.concat([imageUrl]) });
        });
      }
      toast.success('Imagen subida correctamente');
    } catch (err) {
      var errorMsg = err.response?.data?.detail || 'Error al subir imagen. Intente con otra imagen.';
      toast.error(errorMsg);
      console.error('Upload error:', err);
    }
    setUploading(false);
  }

  function removeGalleryImage(index) {
    setFormData(function(prev) {
      var gallery = (prev.gallery || []).filter(function(_, i) { return i !== index; });
      return Object.assign({}, prev, { gallery: gallery });
    });
  }

  function startEditing(workshop) {
    setEditingWorkshop(workshop);
    setFormData({
      name: workshop.name || '',
      description: workshop.description || '',
      workshop_type: workshop.workshop_type || 'otro',
      custom_type: workshop.custom_type || '',
      phone: workshop.phone || '',
      whatsapp: workshop.whatsapp || '',
      email: workshop.email || '',
      website: workshop.website || '',
      address: workshop.address || '',
      city: workshop.city || '',
      region: workshop.region || '',
      modality: workshop.modality || 'presencial',
      price: workshop.price || '',
      price_description: workshop.price_description || '',
      is_free: workshop.is_free || false,
      logo: workshop.logo || '',
      gallery: workshop.gallery || [],
      youtube_video: workshop.youtube_video || '',
      schedule: workshop.schedule || '',
      duration: workshop.duration || '',
      additional_services: workshop.additional_services || [],
      is_public: workshop.is_public || false
    });
    setShowForm(true);
    setActiveTab('info'); // Reset to first tab when opening form
    loadAnalytics(workshop.workshop_id);
    loadPremiumStatus(workshop.workshop_id);
  }

  function startNewWorkshop() {
    setEditingWorkshop(null);
    setFormData(initialFormState);
    setShowForm(true);
    setActiveTab('info'); // Reset to first tab when opening form
    setAnalytics(null);
    setPremiumStatus(null);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingWorkshop(null);
    setFormData(initialFormState);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);

    try {
      var data = Object.assign({}, formData);
      // Convert price to number
      if (data.price) {
        data.price = parseInt(data.price, 10) || 0;
      } else {
        data.price = null;
      }

      if (editingWorkshop) {
        await authAxios.put(API + '/workshop/' + editingWorkshop.workshop_id, data, {
          headers: { Authorization: 'Bearer ' + auth.token }
        });
        toast.success('Taller actualizado');
      } else {
        await authAxios.post(API + '/workshop/register', data, {
          headers: { Authorization: 'Bearer ' + auth.token }
        });
        toast.success('Taller creado exitosamente');
      }

      loadWorkshops();
      cancelForm();
    } catch (e) {
      console.error(e);
      toast.error('Error al guardar el taller');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(workshopId) {
    if (!window.confirm('¿Estás seguro de eliminar este taller?')) return;

    try {
      await authAxios.delete(API + '/workshop/' + workshopId, {
        headers: { Authorization: 'Bearer ' + auth.token }
      });
      toast.success('Taller eliminado');
      loadWorkshops();
    } catch (e) {
      console.error(e);
      toast.error('Error al eliminar el taller');
    }
  }

  async function handlePremiumCheckout(workshopId) {
    try {
      var res = await authAxios.post(API + '/workshop/premium/checkout', {
        workshop_id: workshopId,
        origin_url: window.location.origin,
        plan_type: 'destacado'
      }, {
        headers: { Authorization: 'Bearer ' + auth.token }
      });

      if (res.data.url && res.data.token) {
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = res.data.url;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'token_ws';
        input.value = res.data.token;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Error al procesar el pago');
    }
  }

  if (loading && workshops.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="workshop-dashboard">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-50">Panel de Talleres</h1>
              <p className="text-slate-400 mt-1">Gestiona tus talleres de rehabilitación</p>
            </div>
            {!showForm && (
              <Button onClick={startNewWorkshop} className="bg-emerald-500 hover:bg-emerald-600" data-testid="new-workshop-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Taller
              </Button>
            )}
          </div>

          {showForm ? (
            // Workshop Form
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-50">
                  {editingWorkshop ? 'Editar Taller' : 'Crear Nuevo Taller'}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Completa la información de tu taller para publicarlo en el directorio
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="bg-slate-800 mb-6">
                    <TabsTrigger value="info" className="data-[state=active]:bg-emerald-500">Información</TabsTrigger>
                    <TabsTrigger value="contact" className="data-[state=active]:bg-emerald-500">Contacto</TabsTrigger>
                    <TabsTrigger value="details" className="data-[state=active]:bg-emerald-500">Detalles</TabsTrigger>
                    <TabsTrigger value="media" className="data-[state=active]:bg-emerald-500">Multimedia</TabsTrigger>
                    {editingWorkshop && (
                      <TabsTrigger value="premium" className="data-[state=active]:bg-amber-500">Premium</TabsTrigger>
                    )}
                  </TabsList>

                  <form onSubmit={handleSubmit}>
                    {/* Basic Info Tab */}
                    <TabsContent value="info" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-300">Nombre del taller *</Label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Ej: Taller de Arteterapia"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                            data-testid="workshop-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Tipo de taller *</Label>
                          <Select value={formData.workshop_type} onValueChange={function(v) { handleSwitchChange('workshop_type', v); }}>
                            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              {workshopTypeOptions.map(function(opt) {
                                return <SelectItem key={opt.value} value={opt.value} className="text-slate-200">{opt.label}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {formData.workshop_type === 'otro' && (
                        <div>
                          <Label className="text-slate-300">Especifica el tipo</Label>
                          <Input
                            name="custom_type"
                            value={formData.custom_type}
                            onChange={handleChange}
                            placeholder="Ej: Terapia con caballos"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-300">Descripción</Label>
                          <AIDescriptionButton
                            entityType="workshop"
                            name={formData.name}
                            context={{
                              categoría: formData.category,
                              modalidad: formData.modality,
                              ciudad: formData.city,
                              duración: formData.duration
                            }}
                            onGenerated={function(desc) { setFormData(function(prev) { return Object.assign({}, prev, { description: desc }); }); }}
                          />
                        </div>
                        <Textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Describe tu taller, metodología, beneficios..."
                          className="bg-slate-800 border-slate-700 text-slate-200"
                          data-testid="workshop-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-300">Modalidad</Label>
                          <Select value={formData.modality} onValueChange={function(v) { handleSwitchChange('modality', v); }}>
                            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              <SelectItem value="presencial" className="text-slate-200">Presencial</SelectItem>
                              <SelectItem value="online" className="text-slate-200">Online</SelectItem>
                              <SelectItem value="hibrido" className="text-slate-200">Híbrido</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Región</Label>
                          <Select value={formData.region || ''} onValueChange={function(v) { handleSwitchChange('region', v); }}>
                            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
                              <SelectValue placeholder="Selecciona región" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              {CHILEAN_REGIONS.map(function(r) {
                                return <SelectItem key={r.value} value={r.value} className="text-slate-200">{r.label}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Ciudad</Label>
                          <Select value={formData.city} onValueChange={function(v) { handleSwitchChange('city', v); }}>
                            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200">
                              <SelectValue placeholder="Selecciona ciudad" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              {(formData.region ? getCitiesByRegion(formData.region) : cities).map(function(c) {
                                return <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>;
                              })}
                              <SelectItem value="otra" className="text-slate-200">Otra ciudad</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label className="text-slate-300">Dirección</Label>
                        <Input
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Ej: Av. Providencia 1234, Of. 501"
                          className="bg-slate-800 border-slate-700 text-slate-200"
                        />
                      </div>
                    </TabsContent>

                    {/* Contact Tab */}
                    <TabsContent value="contact" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-300">Teléfono</Label>
                          <Input
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+56 9 1234 5678"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">WhatsApp</Label>
                          <Input
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleChange}
                            placeholder="+56 9 1234 5678"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-300">Email</Label>
                          <Input
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="contacto@taller.cl"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Sitio web</Label>
                          <Input
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            placeholder="https://www.mitaller.cl"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                      </div>
                      
                      {/* Social Media */}
                      <div className="pt-4 border-t border-slate-700">
                        <Label className="text-slate-300 mb-3 block">Redes Sociales</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-slate-400 text-xs">Facebook</Label>
                            <Input
                              name="facebook"
                              value={formData.facebook || ''}
                              onChange={handleChange}
                              placeholder="facebook.com/mitaller"
                              className="bg-slate-800 border-slate-700 text-slate-200"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs">Instagram</Label>
                            <Input
                              name="instagram"
                              value={formData.instagram || ''}
                              onChange={handleChange}
                              placeholder="@mitaller"
                              className="bg-slate-800 border-slate-700 text-slate-200"
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Details Tab */}
                    <TabsContent value="details" className="space-y-6">
                      <div>
                        <Label className="text-slate-300">Público objetivo</Label>
                        <Input
                          name="target_audience"
                          value={formData.target_audience || ''}
                          onChange={handleChange}
                          placeholder="Ej: Adultos en recuperación, familiares, profesionales de salud..."
                          className="bg-slate-800 border-slate-700 text-slate-200"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <Label className="text-slate-300">Horario</Label>
                          <Input
                            name="schedule"
                            value={formData.schedule}
                            onChange={handleChange}
                            placeholder="Ej: Lunes y Miércoles 18:00-20:00"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Duración</Label>
                          <Input
                            name="duration"
                            value={formData.duration}
                            onChange={handleChange}
                            placeholder="Ej: 2 horas por sesión"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Capacidad máxima</Label>
                          <Input
                            name="capacity"
                            type="number"
                            value={formData.capacity || ''}
                            onChange={handleChange}
                            placeholder="Ej: 15"
                            className="bg-slate-800 border-slate-700 text-slate-200"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-slate-300">Requisitos para participar</Label>
                        <Textarea
                          name="requirements"
                          value={formData.requirements || ''}
                          onChange={handleChange}
                          rows={3}
                          placeholder="Describe requisitos previos, materiales necesarios, etc."
                          className="bg-slate-800 border-slate-700 text-slate-200"
                        />
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-xl">
                        <Switch
                          checked={formData.is_free}
                          onCheckedChange={function(v) { handleSwitchChange('is_free', v); }}
                        />
                        <div>
                          <Label className="text-slate-200">Taller gratuito</Label>
                          <p className="text-slate-500 text-sm">Marca esta opción si el taller no tiene costo</p>
                        </div>
                      </div>

                      {!formData.is_free && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-slate-300">Precio (CLP)</Label>
                            <Input
                              name="price"
                              type="number"
                              value={formData.price}
                              onChange={handleChange}
                              placeholder="Ej: 50000"
                              className="bg-slate-800 border-slate-700 text-slate-200"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Descripción del precio</Label>
                            <Input
                              name="price_description"
                              value={formData.price_description}
                              onChange={handleChange}
                              placeholder="Ej: mensual, por sesión, etc."
                              className="bg-slate-800 border-slate-700 text-slate-200"
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <Label className="text-slate-300 mb-3 block">Servicios adicionales</Label>
                        <div className="flex flex-wrap gap-2">
                          {additionalServicesOptions.map(function(service) {
                            var isSelected = (formData.additional_services || []).includes(service);
                            return (
                              <Button
                                key={service}
                                type="button"
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                onClick={function() { handleServiceToggle(service); }}
                                className={isSelected ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                              >
                                {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                                {service}
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-xl">
                        <Switch
                          checked={formData.is_public}
                          onCheckedChange={function(v) { handleSwitchChange('is_public', v); }}
                        />
                        <div>
                          <Label className="text-slate-200">Publicar en directorio</Label>
                          <p className="text-slate-500 text-sm">Tu taller será visible públicamente en el directorio</p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Media Tab */}
                    <TabsContent value="media" className="space-y-6">
                      {/* Logo */}
                      <div>
                        <Label className="text-slate-300 mb-3 block">Logo / Imagen principal</Label>
                        <div className="flex items-center gap-4">
                          {formData.logo ? (
                            <div className="relative">
                              <img src={formData.logo} alt="Logo" className="w-24 h-24 rounded-xl object-cover border border-slate-700" />
                              <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 w-6 h-6" onClick={function() { setFormData(function(prev) { return Object.assign({}, prev, { logo: '' }); }); }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-700 flex items-center justify-center bg-slate-950">
                              <Palette className="w-8 h-8 text-slate-600" />
                            </div>
                          )}
                          <div>
                            <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'logo'); }} className="hidden" id="workshop-logo-upload" />
                            <label htmlFor="workshop-logo-upload">
                              <Button type="button" variant="outline" size="sm" asChild disabled={uploading} className="border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer">
                                <span>{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : <><Upload className="w-4 h-4 mr-2" />Subir logo</>}</span>
                              </Button>
                            </label>
                            <p className="text-xs text-slate-500 mt-2">Recomendado: 200x200px</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-slate-300">Video de YouTube</Label>
                        <Input
                          name="youtube_video"
                          value={formData.youtube_video}
                          onChange={handleChange}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="bg-slate-800 border-slate-700 text-slate-200"
                        />
                      </div>

                      {/* Gallery */}
                      <div>
                        <Label className="text-slate-300 mb-3 block">Galería de fotos</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {(formData.gallery || []).map(function(img, index) {
                            return (
                              <div key={index} className="relative group">
                                <img src={img} alt={'Foto ' + (index + 1)} className="w-full h-24 object-cover rounded-lg border border-slate-700" />
                                <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={function() { removeGalleryImage(index); }}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            );
                          })}
                          <div className="w-full h-24 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center bg-slate-950 cursor-pointer hover:border-purple-500/50 transition-colors" onClick={function() { galleryInputRef.current?.click(); }}>
                            <div className="text-center">
                              <Plus className="w-6 h-6 mx-auto text-slate-500" />
                              <span className="text-xs text-slate-500">Agregar foto</span>
                            </div>
                          </div>
                          <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'gallery'); }} className="hidden" ref={galleryInputRef} />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Sube fotos de tu taller, sesiones, materiales, etc.</p>
                      </div>
                    </TabsContent>

                    {/* Premium Tab */}
                    {editingWorkshop && (
                      <TabsContent value="premium" className="space-y-6">
                        {/* Header */}
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                            <Crown className="w-8 h-8 text-amber-400" />
                          </div>
                          <h3 className="text-2xl font-bold text-slate-50 mb-2">Planes para Talleres</h3>
                          <p className="text-slate-400">Aumenta la visibilidad de tu taller</p>
                        </div>

                        {premiumStatus?.is_premium ? (
                          <Card className="bg-emerald-500/10 border-emerald-500/30">
                            <CardContent className="p-6 text-center">
                              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                              <h4 className="text-xl font-bold text-emerald-400">Plan Destacado Activo</h4>
                              <p className="text-slate-400 mt-1">Vence en {premiumStatus.days_remaining} días</p>
                            </CardContent>
                          </Card>
                        ) : (
                          <>
                            {/* Plans Grid */}
                            <div className="grid md:grid-cols-2 gap-4">
                              {/* Plan Gratuito */}
                              <Card className="bg-slate-900 border-slate-700">
                                <CardContent className="p-5">
                                  <h4 className="text-lg font-bold text-slate-300 mb-1">Gratuito</h4>
                                  <p className="text-xs text-slate-500 mb-3">Presencia básica</p>
                                  <div className="mb-3">
                                    <span className="text-2xl font-bold text-slate-400">$0</span>
                                    <span className="text-slate-500 text-sm"> / mes</span>
                                  </div>
                                  <ul className="space-y-1.5 mb-4 text-xs">
                                    <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Perfil del taller</li>
                                    <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Información de contacto</li>
                                    <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin posición prioritaria</li>
                                  </ul>
                                  <Button variant="outline" disabled className="w-full border-slate-700 text-slate-500 text-sm">
                                    Plan Actual
                                  </Button>
                                </CardContent>
                              </Card>

                              {/* Plan Destacado */}
                              <Card className="bg-gradient-to-br from-emerald-500/10 to-slate-900 border-emerald-500/30 relative overflow-hidden">
                                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-bl-lg">RECOMENDADO</div>
                                <CardContent className="p-5">
                                  <h4 className="text-lg font-bold text-emerald-400 mb-1">Destacado</h4>
                                  <p className="text-xs text-slate-400 mb-3">Posición prioritaria</p>
                                  <div className="mb-3">
                                    <span className="text-2xl font-bold text-slate-50">$19.000</span>
                                    <span className="text-slate-400 text-sm"> / mes</span>
                                  </div>
                                  <ul className="space-y-1.5 mb-4 text-xs">
                                    <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Badge "Destacado"</li>
                                    <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Posición prioritaria</li>
                                    <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Estadísticas de perfil</li>
                                  </ul>
                                  <Button
                                    onClick={function() { handlePremiumCheckout(editingWorkshop.workshop_id); }}
                                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-sm"
                                    data-testid="workshop-premium-btn"
                                  >
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    Activar Plan
                                  </Button>
                                </CardContent>
                              </Card>
                            </div>

                            {/* Plan por Referidos - OCULTO TEMPORALMENTE */}
                            {false && (
                            <Card className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border-teal-500/30">
                              <CardContent className="p-5">
                                <div className="flex flex-col md:flex-row items-center gap-4">
                                  <div className="w-14 h-14 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                                    <Target className="w-7 h-7 text-teal-400" />
                                  </div>
                                  <div className="flex-1 text-center md:text-left">
                                    <h4 className="text-lg font-bold text-slate-50">Plan por Referidos</h4>
                                    <p className="text-sm text-slate-300">
                                      <strong className="text-teal-400">Sin costo inicial.</strong> Solo pagas el 40% de la primera inscripción cuando un participante llega a través de nuestra plataforma.
                                    </p>
                                  </div>
                                  <a href="/contacto?asunto=plan-referidos-taller" className="flex-shrink-0">
                                    <Button className="bg-teal-500 hover:bg-teal-600">
                                      <Phone className="w-4 h-4 mr-2" />
                                      Solicitar
                                    </Button>
                                  </a>
                                </div>
                              </CardContent>
                            </Card>
                            )}
                          </>
                        )}

                        {/* Analytics */}
                        {analytics && (
                          <Card className="bg-slate-800/50 border-slate-700">
                            <CardHeader>
                              <CardTitle className="text-slate-50 flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-emerald-400" />
                                Estadísticas (últimos 30 días)
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-slate-900 rounded-xl">
                                  <p className="text-slate-500 text-sm">Visitas</p>
                                  <p className="text-2xl font-bold text-slate-50">{analytics.views_30d || 0}</p>
                                </div>
                                <div className="p-4 bg-slate-900 rounded-xl">
                                  <p className="text-slate-500 text-sm">Clicks WhatsApp</p>
                                  <p className="text-2xl font-bold text-slate-50">{analytics.whatsapp_clicks_30d || 0}</p>
                                </div>
                                <div className="p-4 bg-slate-900 rounded-xl">
                                  <p className="text-slate-500 text-sm">Contactos</p>
                                  <p className="text-2xl font-bold text-slate-50">{analytics.total_contacts || 0}</p>
                                </div>
                                <div className="p-4 bg-slate-900 rounded-xl">
                                  <p className="text-slate-500 text-sm">Calificación</p>
                                  <p className="text-2xl font-bold text-slate-50 flex items-center gap-1">
                                    {analytics.average_rating?.toFixed(1) || '0.0'}
                                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </TabsContent>
                    )}

                    {/* Preview Section */}
                    <div className="mt-8">
                      <WorkshopPreview data={formData} />
                    </div>

                    {/* Form Actions */}
                    <div className="flex gap-3 mt-8 pt-6 border-t border-slate-800">
                      <Button type="button" variant="outline" onClick={cancelForm} className="border-slate-700 text-slate-300">
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-workshop-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        {editingWorkshop ? 'Guardar cambios' : 'Crear taller'}
                      </Button>
                    </div>
                  </form>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            // Workshops List
            <>
              {workshops.length === 0 ? (
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardContent className="py-16 text-center">
                    <Palette className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-400 mb-2">No tienes talleres registrados</h3>
                    <p className="text-slate-500 mb-6">Crea tu primer taller y publícalo en el directorio</p>
                    <Button onClick={startNewWorkshop} className="bg-emerald-500 hover:bg-emerald-600">
                      <Plus className="w-4 h-4 mr-2" /> Crear mi primer taller
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {workshops.map(function(workshop) {
                    return (
                      <Card key={workshop.workshop_id} className="bg-slate-900/50 border-slate-800 overflow-hidden">
                        {/* Image */}
                        <div className="aspect-video bg-slate-800 relative">
                          {workshop.logo ? (
                            <img src={workshop.logo} alt={workshop.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Palette className="w-12 h-12 text-slate-700" />
                            </div>
                          )}
                          {/* Status badges */}
                          <div className="absolute top-3 left-3 flex gap-2">
                            {workshop.is_premium && (
                              <span className="px-2 py-1 bg-amber-500 text-amber-950 text-xs font-bold rounded-md flex items-center gap-1">
                                <Crown className="w-3 h-3" /> Premium
                              </span>
                            )}
                            {workshop.is_public ? (
                              <span className="px-2 py-1 bg-emerald-500/80 text-emerald-950 text-xs font-bold rounded-md flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Público
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-slate-600 text-slate-200 text-xs font-bold rounded-md flex items-center gap-1">
                                <EyeOff className="w-3 h-3" /> Oculto
                              </span>
                            )}
                          </div>
                        </div>

                        <CardContent className="p-5">
                          <h3 className="font-semibold text-lg text-slate-50 mb-2">{workshop.name}</h3>
                          {workshop.city && (
                            <p className="text-slate-500 text-sm flex items-center gap-1 mb-4">
                              <MapPin className="w-4 h-4" /> {workshop.city}
                            </p>
                          )}

                          {/* Stats */}
                          <div className="flex items-center gap-4 text-slate-400 text-sm mb-4">
                            {workshop.average_rating > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                {workshop.average_rating.toFixed(1)}
                              </span>
                            )}
                            {workshop.total_reviews > 0 && (
                              <span>{workshop.total_reviews} reseñas</span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button onClick={function() { startEditing(workshop); }} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-300">
                              <Edit className="w-4 h-4 mr-1" /> Editar
                            </Button>
                            {workshop.is_public && (
                              <Link to={'/talleres/' + workshop.workshop_id} target="_blank">
                                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </Link>
                            )}
                            <Button onClick={function() { handleDelete(workshop.workshop_id); }} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Workshop Register Page (landing for new users)
export function WorkshopRegister() {
  var auth = useAuth();

  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <Palette className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
              Publica tu Taller en Sin Adicciones
            </h1>
            <p className="text-lg text-slate-400 mb-8">
              Llega a miles de personas buscando actividades terapéuticas complementarias para su proceso de recuperación.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-50 mb-2">Mayor Visibilidad</h3>
                  <p className="text-slate-400 text-sm">Aparece en el directorio de talleres más visitado de Chile</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6 text-center">
                  <MessageCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-50 mb-2">Contacto Directo</h3>
                  <p className="text-slate-400 text-sm">Recibe mensajes y llamadas de personas interesadas</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6 text-center">
                  <BarChart3 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-50 mb-2">Estadísticas</h3>
                  <p className="text-slate-400 text-sm">Conoce el rendimiento de tu perfil con métricas detalladas</p>
                </CardContent>
              </Card>
            </div>

            <Link to="/login">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                Inicia sesión para publicar tu taller
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return <WorkshopDashboard />;
}

export default WorkshopDashboard;
